<template>
  <div>
    <div class="main-content">
      <div class="row">
        <div class="col-md-12">
          <div class="card">
            <div class="card-body">
              <ValidationObserver ref="formObserver" v-slot="{ validate }">
                <b-form @submit.prevent="handleSubmit(validate)">
                  <div class="form-row">
                    <div class="form-group col-md-6">
                      <ValidationProvider name="Name" rules="required" v-slot="{ errors }">
                        <label for="name">
                          <span class="required">*</span> {{ $t("Name") }}:
                        </label>
                        <b-form-input
                          id="name"
                          type="text"
                          v-model="worker.name"
                          :state="errors.length ? false : null"
                          placeholder="Sync 192 — servidor principal"
                        />
                        <b-form-invalid-feedback :state="errors.length">
                          {{ $t(errors[0]) }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </div>

                    <div class="form-group col-md-6">
                      <label for="company">
                        <span class="required">*</span> {{ $t("Company") }}:
                        <small v-if="!podeEscolherEmpresa" class="text-muted">
                          — {{ $t("locked to your company") }}
                        </small>
                      </label>
                      <v-select
                        id="company"
                        :options="companies"
                        :reduce="(company) => company.id"
                        label="name"
                        v-model="worker.company_id"
                        :disabled="!podeEscolherEmpresa"
                        class="vselect"
                        @input="onCompanyChange"
                      />
                    </div>

                    <div class="form-group col-md-6">
                      <ValidationProvider name="Central" rules="required" v-slot="{ errors }">
                        <label for="center">
                          <span class="required">*</span> {{ $t("Central") }}:
                        </label>
                        <v-select
                          id="center"
                          :options="centers"
                          :reduce="(center) => center.id"
                          label="name"
                          v-model="worker.center_id"
                          class="vselect"
                          :class="{ 'is-invalid': errors.length }"
                          @input="onCenterChange"
                        />
                        <span class="invalid-feedback">{{ $t(errors[0]) }}</span>
                      </ValidationProvider>
                    </div>

                    <div class="form-group col-md-6">
                      <ValidationProvider name="Units" rules="required" v-slot="{ errors }">
                        <div class="d-flex justify-content-between align-items-center">
                          <label for="units" class="mb-0">
                            <span class="required">*</span> {{ $t("Units") }}:
                          </label>
                          <a
                            v-if="worker.center_id && units.length"
                            href="#"
                            class="small"
                            @click.prevent="alternarTodasUnidades"
                          >
                            {{
                              todasUnidadesSelecionadas
                                ? $t("Clear selection")
                                : $t("Select all") + " (" + units.length + ")"
                            }}
                          </a>
                        </div>
                        <v-select
                          id="units"
                          multiple
                          :options="units"
                          :reduce="(unit) => unit.id"
                          label="name"
                          v-model="worker.unit_ids"
                          class="vselect"
                          :disabled="!worker.center_id"
                          :placeholder="
                            worker.center_id ? $t('Select') : $t('Select the central first')
                          "
                          :class="{ 'is-invalid': errors.length }"
                        />
                        <span class="invalid-feedback">{{ $t(errors[0]) }}</span>
                        <small v-if="!worker.center_id" class="form-text text-muted">
                          {{ $t("Select the central first") }}.
                        </small>
                        <small
                          v-else-if="!units.length && !carregandoUnidades"
                          class="form-text text-danger"
                        >
                          {{ $t("This central has no units registered") }}.
                        </small>
                      </ValidationProvider>
                    </div>

                    <div class="form-group col-md-3">
                      <label for="priority">
                        <span class="required">*</span> {{ $t("Priority") }}:
                      </label>
                      <b-form-input id="priority" type="number" min="1" max="999" v-model.number="worker.priority" />
                      <small class="text-muted">{{ $t("1 is the highest") }}</small>
                    </div>

                    <div class="form-group col-md-3">
                      <label for="sync">
                        <span class="required">*</span> {{ $t("Synchronize every (s)") }}:
                      </label>
                      <b-form-input id="sync" type="number" min="10" max="600" v-model.number="worker.sync_interval_seconds" />
                    </div>

                    <div class="form-group col-md-3">
                      <label for="heartbeat">
                        <span class="required">*</span> {{ $t("Heartbeat (s)") }}:
                      </label>
                      <b-form-input id="heartbeat" type="number" min="15" max="600" v-model.number="worker.heartbeat_interval_seconds" />
                    </div>

                    <div class="form-group col-md-3">
                      <label for="threshold">
                        <span class="required">*</span> {{ $t("Offline after (s)") }}:
                      </label>
                      <b-form-input
                        id="threshold"
                        type="number"
                        min="30"
                        max="3600"
                        v-model.number="worker.offline_threshold_seconds"
                        :class="{ 'is-invalid': janela.nivel === 'erro' }"
                      />
                    </div>

                    <div class="form-group col-md-12">
                      <h6 class="modos-titulo">{{ $t("Position history") }}</h6>
                      <p class="modos-ajuda">
                        {{ $t("The Movias real-time sync follows the assigned units and has no switch. The modes below are optional and start disabled.") }}
                      </p>
                    </div>

                    <div class="form-group col-md-6">
                      <b-form-checkbox v-model="worker.historico_app_habilitado" switch>
                        {{ $t("Save app position history") }}
                      </b-form-checkbox>
                      <b-form-input
                        class="mt-1"
                        type="number"
                        min="30"
                        max="3600"
                        v-model.number="worker.historico_app_interval_seconds"
                        :disabled="!worker.historico_app_habilitado"
                      />
                      <small class="text-muted">{{ $t("Interval in seconds") }}</small>
                    </div>

                    <div class="form-group col-md-6">
                      <b-form-checkbox v-model="worker.historico_movias_habilitado" switch>
                        {{ $t("Save Movias position history") }}
                      </b-form-checkbox>
                      <b-form-input
                        class="mt-1"
                        type="number"
                        min="60"
                        max="3600"
                        v-model.number="worker.historico_movias_interval_seconds"
                        :disabled="!worker.historico_movias_habilitado"
                      />
                      <small class="text-muted">{{ $t("Interval in seconds") }}</small>
                    </div>

                    <div class="form-group col-md-12">
                      <b-form-checkbox v-model="worker.rastreador_habilitado" switch>
                        {{ $t("Receive Tags") }}
                      </b-form-checkbox>
                      <small class="text-muted">
                        {{ $t("GT06 Tags are received by the machine that uses this worker key.") }}
                      </small>
                    </div>

                    <div class="form-group col-md-12">
                      <b-form-checkbox v-model="worker.pulsus_habilitado" switch>
                        {{ $t("Receive Pulsus") }}
                      </b-form-checkbox>
                      <small class="text-muted">
                        {{ $t("Pulsus positions and history are fetched by the machine that uses this worker key. Off: Frotas fetches them itself.") }}
                      </small>
                    </div>

                    <div class="form-group col-md-12">
                      <div class="alert" :class="janela.classe" role="alert">
                        <div>
                          <strong>{{ $t("If this worker goes down") }}</strong>,
                          {{ $t("its units stay up to") }}
                          <strong>{{ janela.texto }}</strong>
                          {{ $t("without updating before the backup routine takes over") }}.
                        </div>
                        <small class="d-block mt-1">
                          {{ worker.heartbeat_interval_seconds }}s {{ $t("of heartbeat") }} +
                          {{ worker.offline_threshold_seconds }}s {{ $t("of tolerance") }} +
                          {{ defaults.backup_interval_seconds }}s {{ $t("of the backup cycle") }}.
                          {{ janela.multiplicador }}
                        </small>
                        <small v-if="janela.aviso" class="d-block mt-1">
                          <strong>{{ janela.aviso }}</strong>
                        </small>
                      </div>
                    </div>

                    <div class="form-group col-md-3">
                      <label for="status">{{ $t("Active") }}:</label>
                      <b-form-select
                        id="status"
                        v-model="worker.status"
                        :options="[
                          { value: true, text: $t('Yes') },
                          { value: false, text: $t('No') },
                        ]"
                      />
                    </div>
                  </div>

                  <div class="row justify-content-end mt-2">
                    <div class="col-md-3 text-right">
                      <b-button variant="secondary" @click="$router.push('/workers')">
                        {{ $t("Cancel") }}
                      </b-button>
                      <b-button
                        type="submit"
                        variant="primary"
                        class="ml-1"
                        :disabled="salvando || janela.nivel === 'erro'"
                      >
                        {{ $t("Save") }}
                      </b-button>
                    </div>
                  </div>
                </b-form>
              </ValidationObserver>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";
import { ValidationObserver, ValidationProvider, extend } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import {
  BButton,
  BForm,
  BFormInput,
  BFormSelect,
  BFormInvalidFeedback,
} from "bootstrap-vue";

extend("required", required);

export default {
  name: "workerForm",
  components: {
    vSelect,
    ValidationObserver,
    ValidationProvider,
    BButton,
    BForm,
    BFormInput,
    BFormSelect,
    BFormInvalidFeedback,
  },
  data() {
    return {
      worker: {
        name: "",
        company_id: null,
        center_id: null,
        unit_ids: [],
        priority: 1,
        sync_interval_seconds: 30,
        heartbeat_interval_seconds: 60,
        offline_threshold_seconds: 180,
        historico_app_habilitado: false,
        historico_app_interval_seconds: 60,
        historico_movias_habilitado: false,
        historico_movias_interval_seconds: 300,
        rastreador_habilitado: false,
        pulsus_habilitado: false,
        status: true,
      },
      centralAplicada: null,
      companies: [],
      centers: [],
      units: [],
      defaults: {
        backup_interval_seconds: 30,
        min_threshold_multiplier: 2,
        recommended_threshold_multiplier: 3,
        clock_drift_critical_seconds: 30,
        can_choose_company: false,
      },
      carregandoUnidades: false,
      salvando: false,
    };
  },
  computed: {
    podeEscolherEmpresa() {
      return this.defaults.can_choose_company === true;
    },
    todasUnidadesSelecionadas() {
      return (
        this.units.length > 0 && this.worker.unit_ids.length === this.units.length
      );
    },
    janela() {
      const heartbeat = Number(this.worker.heartbeat_interval_seconds || 0);
      const tolerancia = Number(this.worker.offline_threshold_seconds || 0);
      const retaguarda = Number(this.defaults.backup_interval_seconds || 30);
      const minimo = Number(this.defaults.min_threshold_multiplier || 2);
      const recomendado = Number(this.defaults.recommended_threshold_multiplier || 3);

      const total = heartbeat + tolerancia + retaguarda;
      const proporcao = heartbeat > 0 ? tolerancia / heartbeat : 0;

      const resultado = {
        texto: this.formatarDuracao(total),
        multiplicador: `${this.$t("Tolerance is")} ${proporcao.toFixed(1)}x ${this.$t("the heartbeat")}.`,
        classe: "alert-secondary",
        nivel: "ok",
        aviso: null,
      };

      if (heartbeat > 0 && proporcao < minimo) {
        resultado.nivel = "erro";
        resultado.classe = "alert-danger";
        resultado.aviso = `${this.$t("Tolerance must be at least")} ${minimo}x ${this.$t(
          "the heartbeat"
        )} (${heartbeat * minimo}s). ${this.$t(
          "Below that the worker flips between online and offline forever"
        )}.`;

        return resultado;
      }

      if (heartbeat > 0 && proporcao < recomendado) {
        resultado.classe = "alert-warning";
        resultado.aviso = this.$t(
          "Short tolerance: a network hiccup may mark this worker as down and send a needless alert email"
        );

        return resultado;
      }

      if (total > 600) {
        resultado.classe = "alert-warning";
        resultado.aviso = this.$t("Long lag window: check whether this is intended");
      }

      return resultado;
    },
  },
  mounted() {
    this.getDefaults();
    this.getCompanies();

    const id = this.$route.params.id;

    if (id) {
      this.getWorker(id);
    } else {
      this.worker.company_id = this.$store.state.user.company_id;
      this.getCenters();
    }
  },
  methods: {
    formatarDuracao(segundos) {
      if (segundos < 60) {
        return `${segundos}s`;
      }

      const minutos = segundos / 60;

      return `~${minutos.toFixed(1).replace(".", ",")} min`;
    },
    async handleSubmit(validate) {
      const isValid = await validate();

      if (isValid && this.janela.nivel !== "erro") {
        this.save();
      }
    },
    onCompanyChange() {
      this.worker.center_id = null;
      this.centralAplicada = null;
      this.worker.unit_ids = [];
      this.units = [];
      this.getCenters();
    },
    // O v-select emite @input tambem quando o valor e atribuido pelo codigo, e
    // nao so quando o usuario escolhe. Sem esta guarda, carregar um worker para
    // editar dispara este handler e apaga as unidades recem-carregadas: o campo
    // aparece vazio e, como unit_ids e obrigatorio, o usuario e forcado a
    // repreencher — e o sync() do backend substitui a lista inteira.
    onCenterChange(centerId) {
      const escolhida = centerId ?? null;

      if (escolhida === this.centralAplicada) {
        return;
      }

      this.centralAplicada = escolhida;
      this.worker.unit_ids = [];
      this.getUnits();
    },
    alternarTodasUnidades() {
      this.worker.unit_ids = this.todasUnidadesSelecionadas
        ? []
        : this.units.map((unit) => unit.id);
    },
    getDefaults() {
      const api = `${this.$store.state.api}workers/form/defaults`;

      axios
        .get(api)
        .then((response) => {
          this.defaults = { ...this.defaults, ...response.data };
        })
        .catch((error) => this.$error(error));
    },
    getCompanies() {
      const api = `${this.$store.state.api}companies`;

      axios
        .get(api)
        .then((response) => {
          this.companies = response.data.data;
        })
        .catch(() => {});
    },
    getCenters() {
      let api = `${this.$store.state.api}workers/form/centers`;

      if (this.worker.company_id) {
        api += `?company_id=${this.worker.company_id}`;
      }

      axios
        .get(api)
        .then((response) => {
          this.centers = response.data;
        })
        .catch((error) => this.$error(error));
    },
    getUnits() {
      if (!this.worker.center_id) {
        this.units = [];
        return;
      }

      const api = `${this.$store.state.api}workers/form/units?center_id=${this.worker.center_id}`;

      this.carregandoUnidades = true;

      axios
        .get(api)
        .then((response) => {
          this.units = response.data;
        })
        .catch((error) => this.$error(error))
        .then(() => {
          this.carregandoUnidades = false;
        });
    },
    getWorker(id) {
      const api = `${this.$store.state.api}workers/${id}`;

      axios
        .get(api)
        .then((response) => {
          const dados = response.data.data ? response.data.data[0] : response.data;

          this.worker = {
            id: dados.id,
            name: dados.name,
            company_id: dados.company_id,
            center_id: dados.center_id,
            unit_ids: (dados.units || [])
              .filter((unit) => !unit.center_mismatch)
              .map((unit) => unit.id),
            priority: dados.priority,
            sync_interval_seconds: dados.sync_interval_seconds,
            heartbeat_interval_seconds: dados.heartbeat_interval_seconds,
            offline_threshold_seconds: dados.offline_threshold_seconds,
            historico_app_habilitado: Boolean(dados.historico_app_habilitado),
            historico_app_interval_seconds: dados.historico_app_interval_seconds ?? 60,
            historico_movias_habilitado: Boolean(dados.historico_movias_habilitado),
            historico_movias_interval_seconds: dados.historico_movias_interval_seconds ?? 300,
            rastreador_habilitado: Boolean(dados.rastreador_habilitado),
            pulsus_habilitado: Boolean(dados.pulsus_habilitado),
            status: Boolean(dados.status),
          };

          this.centralAplicada = dados.center_id;

          this.getCenters();
          this.getUnits();
        })
        .catch((error) => this.$error(error));
    },
    save() {
      this.salvando = true;

      let api = `${this.$store.state.api}workers`;
      const payload = { ...this.worker };

      if (this.worker.id) {
        payload._method = "PUT";
        api += `/${this.worker.id}`;
      }

      axios
        .post(api, payload)
        .then((response) => {
          this.$message(
            this.$t("Success"),
            this.$t("Information saved successfully"),
            "success"
          );

          const id = response.data.worker ? response.data.worker.id : this.worker.id;

          this.$router.push(`/workers/view/${id}?novo=1`);
        })
        .catch((error) => this.$error(error))
        .then(() => {
          this.salvando = false;
        });
    },
  },
};
</script>

<style scoped>
.modos-titulo {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  opacity: 0.45;
  margin: 0.5rem 0 0.35rem;
}

.modos-ajuda {
  font-size: 0.85rem;
  opacity: 0.6;
  margin: 0 0 0.25rem;
}
</style>
